import { useMutation } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'
import {
    selectCompanyMemberList,
    selectCompanyLoading,
    selectCompanyId,
    updateMembership,
    deleteMember as deleteCompanyMember,
} from '../store/company'
import { isEqual } from '../util/isEqual'
import { CompanyMember } from '../models/client/company'
import { companyClient } from '../clients/CompanyClient'
import { selectUserToken } from '../store/user'
import { ApiError } from '../util/errors/ApiError'
import { useReduxDispatch } from '../store/index'
import { DraftUser } from '../models/client/user'

type Params = {
    onEditSuccess?: () => void
    onEditError?: (error: Error) => void
    onDeleteSuccess?: () => void
    onDeleteError?: (error: Error) => void
    onAddError?: (error: Error) => void
    onAddSuccess?: () => void
}

type Return = {
    companyMembers: CompanyMember[]
    membersAreLoading: boolean
    editIsLoading: boolean
    deleteIsLoading: boolean
    addIsLoading: boolean
    deleteUser: (memberId: string) => Promise<void>
    editUser: (memberDraft: DraftUser, memberId: string) => Promise<CompanyMember>
    addUser: (memberDraft: DraftUser) => Promise<CompanyMember>
}

export const useCompanyMembers = (params?: Params): Return => {
    const dispatch = useReduxDispatch()
    const token = useSelector(selectUserToken, shallowEqual)
    const companyMembers = useSelector(selectCompanyMemberList, isEqual)
    const companyId = useSelector(selectCompanyId, shallowEqual)
    const membersAreLoading = useSelector(selectCompanyLoading, shallowEqual)

    const { mutateAsync: editMember, status: editMemberStatus } = useMutation(
        companyClient.editCompanyMember,
        {
            onSuccess: () => params?.onEditSuccess?.(),
            onError: (possibleError) => {
                if (possibleError instanceof Error) {
                    params?.onEditError?.(possibleError)
                }
            },
        }
    )

    const { mutateAsync: addMember, status: addMemberStatus } = useMutation(
        companyClient.addCompanyMember,
        {
            onSuccess: () => params?.onAddSuccess?.(),
            onError: (possibleError) => {
                if (possibleError instanceof Error) {
                    params?.onAddError?.(possibleError)
                }
            },
        }
    )

    const { mutateAsync: deleteMember, status: deleteMemberStatus } = useMutation(
        companyClient.deleteCompanyMember,
        {
            onSuccess: () => params?.onDeleteSuccess?.(),
            onError: (possibleError) => {
                if (possibleError instanceof Error) {
                    params?.onDeleteError?.(possibleError)
                }
            },
        }
    )

    return {
        companyMembers,
        membersAreLoading,
        editIsLoading: editMemberStatus === 'loading',
        deleteIsLoading: deleteMemberStatus === 'loading',
        addIsLoading: addMemberStatus === 'loading',
        deleteUser: async (memberId) => {
            if (!token) {
                throw new ApiError({ type: 'Unauthorized' })
            }

            await deleteMember({ token, memberId })

            dispatch(deleteCompanyMember(memberId))
        },
        editUser: async (memberDraft, memberId) => {
            if (!companyId || !token) {
                throw new ApiError({ type: 'Unauthorized' })
            }

            const nextMember = await editMember({
                token,
                draft: memberDraft,
                companyId,
                memberId,
            })

            dispatch(updateMembership(nextMember))

            return nextMember
        },
        addUser: async (draft) => {
            if (!companyId || !token) {
                throw new ApiError({ type: 'Unauthorized' })
            }

            const nextMember = await addMember({ draft, token, companyId })

            dispatch(updateMembership(nextMember))

            return nextMember
        },
    }
}
